// Full-document render of the toppers ProductLinePage (ROUTABLE): PageSkeleton
// (the React document shell) wrapping the ``product_line_page.html``
// layout -- the pdp-hero (the OverlayBanner + PDPHero islands + the modular
// configurator) inside ``#product-detail-container`` / ``#product-detail``, then
// the consolidated body island carrying the four id-keyed body SSR side-channels
// plus the page-level reviews / page_num_reviews channels.
//
// Server-only values (image renditions, the configurator SSR envelope, the
// per-block reviews / change-selector / conditional SSR state) are resolved
// server-side and threaded as scalar / nested-template / id-keyed side-channels.
//
// The iovation scripts are injected imperatively from a post-hydration effect
// (IovationScripts) rather than as declarative React <script>s: React 19 would
// hoist a declarative ``<script async src>`` to <head>, letting it execute before
// the body IGLOO config on a warm cache and silently degrading fingerprinting.
import React from "react";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";

import { Context, templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { ProductLinePageBody } from "../components/ProductLinePageBody";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";
import { Template as OverlayBannerAssembly } from "./OverlayBannerAssemblyTemplate";
import { Template as PDPHero } from "./PDPHeroTemplate";

// Mutable reactivated props (not Immutable<>): this template composes the in-tree
// SiteHeader / SiteFooter chain via PageSkeleton, which is typed with mutable
// reactivated props. The islands accept Immutable props, to which these are
// assignable.
type IProps = templates.ProductLinePageTemplate;

export const Template = (props: IProps): React.JSX.Element => {
    const { page } = props;
    const chrome = React.useContext(Context).chrome;

    // ``class='pdp-hero {% if self.overlay_banner %} pdp-hero--relative{% endif %}'``
    // -- StreamValue truthiness (an empty stream is falsy).
    const hasOverlayBanner =
        !!page.overlay_banner && page.overlay_banner.value.length > 0;

    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div id="product-detail-container">
                <div id="product-detail">
                    <div
                        className={`pdp-hero${
                            hasOverlayBanner ? " pdp-hero--relative" : ""
                        }`}
                    >
                        <OverlayBannerAssembly
                            {...props.overlay_banner_assembly}
                        />
                        <PDPHero {...props.pdp_hero} />
                        <div className="pdp-hero__content l-capped-width">
                            <ModularConfigurator {...props.configurator} />
                        </div>
                    </div>

                    {/* The consolidated PLP body assembly: the ``modules``
                        StreamField rendered through the shared component map,
                        threading the id-keyed body SSR side-channels. */}
                    <ProductLinePageBody
                        page={page}
                        reviews={props.reviews}
                        page_num_reviews={props.page_num_reviews}
                        change_variant_ssr={props.change_variant_ssr}
                        change_root_ssr={props.change_root_ssr}
                        root_conditional_ssr={props.root_conditional_ssr}
                        variant_conditional_ssr={props.variant_conditional_ssr}
                    />
                </div>
            </div>

            {/* iovation (page-scoped): injected post-hydration to keep the
                IGLOO -> snare -> loader execution order (see IovationScripts). */}
            <IovationScripts
                iovationUrl={props.iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
